import { type FunctionComponent, useEffect, useMemo, useState } from 'react';

import { NumericInput } from '@blueprintjs/core';
import { RuleSetFilterDtoStatesEnum } from 'generated-sources/openapi';

import i18n from 'i18n/i18n';
import { EXECUTION_OUTCOME_TYPE } from 'interfaces/enums';
import { type ForecastFilter } from 'interfaces/Interfaces';
import TableFilter from 'lib/Table/TableFilter/TableFilter';
import { type Filter, FilterType } from 'lib/Table/types/FilterModel';
import { getAllDunningLevels, getBasicRuleSets } from 'services/ApiService';
import { testAttribute } from 'util/Util';

import { EXECUTION_OUTCOME_LABEL_KEY } from './forecastActionTypes';
import { ExecutionFilterMultiSelectInput } from '../../DunningActionPage/ExecutionFilters';
import { alertToast } from '../../Toast/AlertToast';

export const MAX_FORECAST_DAY_OFFSET = 1;
export const DEFAULT_FORECAST_FILTER: ForecastFilter = { forecastDayOffset: 1 };

interface Selectable {
  key: string;
  label: string;
}

interface ForecastFiltersProps {
  filter: ForecastFilter;
  onRefresh: (filter: ForecastFilter) => void;
  onReset: () => void;
}

function toSelectedItems(items: Selectable[], keys?: string[]) {
  return items.filter(item => keys?.includes(item.key));
}

function toForecastFilter(values: Record<string, any>): ForecastFilter {
  const toKeys = (selectedItems?: Selectable[]) => {
    const keys = selectedItems?.map(item => item.key);
    return keys?.length ? keys : undefined;
  };

  return {
    forecastDayOffset: values.forecastDayOffset ?? DEFAULT_FORECAST_FILTER.forecastDayOffset,
    rulesetIds: toKeys(values.rulesetIds),
    dunningLevelNames: toKeys(values.dunningLevelNames),
    actionTypes: toKeys(values.actionTypes),
  };
}

const ForecastFilters: FunctionComponent<ForecastFiltersProps> = ({ filter, onRefresh, onReset }) => {
  const [ruleSets, setRuleSets] = useState<Selectable[]>([]);
  const [dunningLevels, setDunningLevels] = useState<Selectable[]>([]);

  useEffect(() => {
    let active = true;
    Promise.all([getBasicRuleSets({ states: [RuleSetFilterDtoStatesEnum.Active] }), getAllDunningLevels()])
      .then(([availableRuleSets, availableDunningLevels]) => {
        if (!active) return;
        setRuleSets(availableRuleSets.map(ruleSet => ({ key: ruleSet.rulesetId, label: ruleSet.name })));
        setDunningLevels(availableDunningLevels.map(level => ({ key: level.name, label: level.name })));
      })
      .catch(error => {
        if (active) alertToast(error);
      });

    return () => {
      active = false;
    };
  }, []);

  const actionTypes = useMemo(
    () =>
      Object.values(EXECUTION_OUTCOME_TYPE).map(actionType => ({
        key: actionType,
        label: i18n.t(EXECUTION_OUTCOME_LABEL_KEY[actionType]),
      })),
    [],
  );

  const filterDefinitions: Filter[] = [
    {
      key: 'forecastDayOffset',
      name: i18n.t('dunning-forecast.filters.forecast-day'),
      type: FilterType.FIXED,
      render: props => (
        <div className="dunning-forecast__forecast-day-input">
          <NumericInput
            data-test-id={testAttribute('xcy1', 'dunning-forecast.forecast-day')}
            min={1}
            max={MAX_FORECAST_DAY_OFFSET}
            stepSize={1}
            value={props.value ?? DEFAULT_FORECAST_FILTER.forecastDayOffset}
            onValueChange={value => {
              if (Number.isInteger(value)) {
                props.onChange(Math.max(1, Math.min(MAX_FORECAST_DAY_OFFSET, value)));
              }
            }}
          />
          <span>
            {i18n.t('dunning-forecast.filters.working-days', {
              count: props.value ?? DEFAULT_FORECAST_FILTER.forecastDayOffset,
            })}
          </span>
        </div>
      ),
    },
    {
      key: 'rulesetIds',
      name: i18n.t('dunning-forecast.filters.procedure'),
      type: FilterType.DYNAMIC,
      render: props => <ExecutionFilterMultiSelectInput {...props} selectables={ruleSets} />,
    },
    {
      key: 'dunningLevelNames',
      name: i18n.t('dunning-forecast.filters.dunning-level'),
      type: FilterType.DYNAMIC,
      render: props => <ExecutionFilterMultiSelectInput {...props} selectables={dunningLevels} />,
    },
    {
      key: 'actionTypes',
      name: i18n.t('dunning-forecast.filters.action-type'),
      type: FilterType.DYNAMIC,
      render: props => <ExecutionFilterMultiSelectInput {...props} selectables={actionTypes} />,
    },
  ];

  const filterValues = {
    forecastDayOffset: filter.forecastDayOffset,
    rulesetIds: toSelectedItems(ruleSets, filter.rulesetIds),
    dunningLevelNames: toSelectedItems(dunningLevels, filter.dunningLevelNames),
    actionTypes: toSelectedItems(actionTypes, filter.actionTypes),
  };

  return (
    <TableFilter
      i18n={{
        addFilter: i18n.t('filtered-table.add'),
        resetFilters: i18n.t('dunning-forecast.filters.reset'),
        search: i18n.t('dunning-forecast.filters.refresh'),
      }}
      values={filterValues}
      filterDefinitions={filterDefinitions}
      onChange={values => onRefresh(toForecastFilter(values))}
      onReset={onReset}
    />
  );
};

export default ForecastFilters;
